import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    PRODUCTS,
    PRODUCT_CATEGORIES,
    SOLUTIONS_BY_INDUSTRY,
    SOLUTIONS_BY_CAPABILITY,
    ProductCategory,
    ProductItem,
    launchUrl,
} from "../data/productsData";
import { loadProducts } from "../services/productSource";
import { loadSolutions } from "../services/platformSource";
import "../styles/revamp.css";
import Seo from "../components/Seo";

/**
 * The product catalog.
 *
 * Filter state lives in the URL rather than component state so a filtered view
 * is a link someone can send — which is the whole point of a catalog a sales
 * team uses in front of a prospect. The Solutions menu links straight in here
 * with ?solution=..., so both mental models land on the same page.
 */

const ALL = "all";

const Products: React.FC = () => {
    const [products, setProducts] = useState<ProductItem[]>(PRODUCTS);
    const [solutionsList, setSolutionsList] = useState<any[]>([
        ...SOLUTIONS_BY_INDUSTRY,
        ...SOLUTIONS_BY_CAPABILITY,
    ]);
    const [params, setParams] = useSearchParams();

    const category = params.get("category") ?? ALL;
    const solution = params.get("solution");

    useEffect(() => {
        let cancelled = false;
        loadProducts().then((next) => {
            if (!cancelled) setProducts(next);
        });
        loadSolutions().then(({ industry, capability }) => {
            if (!cancelled) setSolutionsList([...industry, ...capability]);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const activeSolution = useMemo(
        () => solutionsList.find((s) => s.slug === solution),
        [solutionsList, solution]
    );

    const visible = useMemo(() => {
        if (activeSolution) {
            return products.filter((p) => activeSolution.productSlugs.includes(p.slug));
        }
        if (category !== ALL) {
            return products.filter((p) => p.category === category);
        }
        return products;
    }, [products, category, activeSolution]);

    const setCategory = (next: string) => {
        // Choosing a category clears a solution filter rather than intersecting
        // them — two filters that can produce an empty page is a worse
        // experience than one that always shows something.
        const p = new URLSearchParams();
        if (next !== ALL) p.set("category", next);
        setParams(p, { replace: true });
    };

    return (
        <>
            <Seo
                title={activeSolution ? activeSolution.title : "Platform"}
                description={
                    activeSolution
                        ? activeSolution.description
                        : "Production systems across autonomous agents, computer vision, voice, business messaging, infrastructure telemetry and hospitality operations."
                }
                path="/products"
            />
            <main className="mx-catalog">
                <div className="container">
                    <p className="mx-eyebrow">Platform</p>
                    <h1 className="mx-suite-title">
                        {activeSolution ? activeSolution.title : "Built to run, not to demo."}
                    </h1>
                    <p className="mx-catalog-lede">
                        {activeSolution
                            ? activeSolution.description
                            : "Production software across autonomous agents, computer vision, voice, business messaging, infrastructure telemetry and hospitality operations."}
                    </p>

                    <div className="mx-filters" role="group" aria-label="Filter products by category">
                        <button
                            type="button"
                            className="mx-tab"
                            data-active={category === ALL && !activeSolution}
                            onClick={() => setCategory(ALL)}
                        >
                            All products
                        </button>
                        {PRODUCT_CATEGORIES.map((c: ProductCategory) => (
                            <button
                                key={c}
                                type="button"
                                className="mx-tab"
                                data-active={category === c && !activeSolution}
                                onClick={() => setCategory(c)}
                            >
                                {c}
                            </button>
                        ))}
                    </div>

                    {activeSolution && (
                        <p className="mx-catalog-clear">
                            Showing products for {activeSolution.title}.{" "}
                            <button type="button" className="mx-linkish" onClick={() => setCategory(ALL)}>
                                Show all products
                            </button>
                        </p>
                    )}

                    <div className="mx-catalog-grid">
                        {visible.map((p) => (
                            <article
                                className="mx-card"
                                key={p.slug}
                                style={{ ["--mx-dot-color" as any]: p.accent }}
                            >
                                <p className="mx-eyebrow">
                                    <span className="mx-dot" /> {p.category}
                                </p>
                                <h2 className="mx-card-title">{p.name}</h2>
                                <p className="mx-card-body">{p.tagline}</p>

                                <div className="mx-card-actions">
                                    <Link className="mx-btn mx-btn-primary" to={`/products/${p.slug}`}>
                                        View details
                                    </Link>
                                    {launchUrl(p) && (
                                        <a
                                            className="mx-launch"
                                            href={launchUrl(p)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Launch ${p.name} in a new tab`}
                                        >
                                            Launch ↗
                                        </a>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </main>
        </>
    );
};

export default Products;
